import { Component, OnInit, OnDestroy, HostListener, Input } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable, filter, map, merge, of, takeUntil, timer, Subscription, Subject } from 'rxjs';

import type { Breadcrumb } from 'src/app/core/models/breadcrumb.model';
import {
  selectLatestCopyInformation,
  selectLatestCreatedId,
  selectLatestDeletedIdList,
  selectProjectEntityTree,
  selectProjectEntityLoading,
  LatestCopyInformation,
  selectLastSelectedHoodId,
  selectLastSelectedModuleId,
  selectLastSelectedEntityId,
  selectProjectLoaded,
  selectProjectFeedbackMessages
} from '../../store/project-data/project-data.selectors';
import { ProjectDataActions } from '../../store/project-data/project-data.actions';
import {
  selectMultiEditEnabled,
  selectMultiEditEntityTypeKey,
  selectMultiEditSelectedEntityIds,
  selectMultiEditSelectedHoodIds,
  selectUIProductSidebarOpened,
  selectUIProductSidebarRightOpened
} from '../../store/product-view/product-view.selectors';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import type { TreeEntity } from '../../../core/models/entity.model';
import { EntityTypesActions } from '../../../core/store/entity-types/entity-types.actions';
import { selectEntityTypesState } from '../../../core/store/entity-types/entity-types.selectors';
import { TranslateService } from '@ngx-translate/core';
import { ProjectEntityService } from '../../../core/services/project.service';
import { withLatestFrom } from 'rxjs/operators';
import { ProductViewActions } from '../../store/product-view/product-view.actions';
import { selectProductPageProductTreeData } from '../../components/product-tree/product-tree.selectors';
import { ActiconInteractiveComponentHandler } from '../../../visualize3d/v3d-components/acticon-interactive/acticon-interactive-component-handler';
import { ProductTreeTraverseService } from '../../../core/services/productTreeTraverse.service';
import { select3DViewStatus, VisualProductViewStatus } from '../../store/project-data/project-data-3d.selectors';
import { PropertyGroupType } from 'src/app/core/models/property-group-type.model';
import { ProductContextActions } from '../../store/product-context/product-context.actions';
import { EntityTypeKey } from 'src/app/shared/constants/entity-type-keys';

@Component({
  selector: 'app-product-view',
  templateUrl: './product-view.component.html',
  styleUrls: ['./product-view.component.scss'],
  standalone: false
})
export class ProductViewComponent implements OnInit, OnDestroy {
  projectEntity$: Observable<TreeEntity>;
  projectTreeEntity$: Observable<TreeEntity>;
  loading$: Observable<boolean>;
  showLoading$ = of(false);
  entityTypeNames$: Observable<Record<string, string>>;
  panelErrorIds$: Observable<string[]>;

  multiEditHoodIds$: Observable<string[]>;

  projectLoaded$: Observable<boolean>;

  emptyEntitySubscription: Subscription;

  latestCreatedId$: Observable<string | null>;
  latestCopiedId$: Observable<LatestCopyInformation>;
  latestDeletedIdList$: Observable<string[]>;

  multiEditEnabled$: Observable<boolean>;
  show3DStatus$: Observable<VisualProductViewStatus>;

  projectId = '';
  productId = '';
  breadcrumbs: Breadcrumb[];
  sidebarOpened$: Observable<boolean>;
  sidebarRightOpened$: Observable<boolean>;

  sidenavSettings = {
    fixed: true,
    top: 157,
    bottom: 0,
    height: 100,
    maxHeightProductTree: 100
  };

  headerHeight = 157;
  footerHeight = 160;

  showInSidebar: boolean;

  private subscriptions: Subscription[] = [];

  private entity3DClicked$ = new Subject<string>();

  @Input() filterGroups: any[];

  @HostListener('window:resize') onWindowResize() {
    this.setSidenavSettings();
  }

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    breakpointObserver: BreakpointObserver,
    private translateService: TranslateService,
    private projectEntityService: ProjectEntityService,
    private interactiveComponentHandler: ActiconInteractiveComponentHandler
  ) {
    this.projectLoaded$ = this.store.select(selectProjectLoaded);
    this.store.dispatch(ProjectDataActions.set_project_loaded({ loaded: false }));

    this.store.dispatch(ProductContextActions.set_context({ context: 'Hood' }));
    this.subscriptions.push(
      this.router.events.subscribe((e: any) => {
        if (e instanceof NavigationEnd) {
          this.initializeComponent();
        }
      })
    );
    breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium]).subscribe(result => {
      if (result.matches) {
        this.showInSidebar = false;
      }
    });
    breakpointObserver.observe([Breakpoints.Large, Breakpoints.XLarge]).subscribe(result => {
      if (result.matches) {
        this.showInSidebar = true;
      }
    });

    this.projectEntity$ = this.store.select(selectProjectEntityTree).pipe(
      withLatestFrom(this.store.select(selectEntityTypesState)),
      map(([project, entityTypesState]) => {
        if (!project || !entityTypesState.loaded) {
          return null;
        }

        // Redirect to hood entity if a line id is entered in url
        const currentEntity = this.getSelectedEntity(project);
        if (
          currentEntity &&
          (currentEntity.EntityTypeKey === EntityTypeKey.GastroFlowHoodLine ||
            currentEntity.EntityTypeKey === EntityTypeKey.GastroFlowHoodConnectionLine)
        ) {
          this.router.navigate([`/projects/${project.Id}/products`], { queryParams: { selectedId: currentEntity.ParentId } });
        }

        if (currentEntity) {
          if (currentEntity.EntityTypeKey === EntityTypeKey.GastroFlowHood) {
            this.store.dispatch(ProjectDataActions.set_last_selected_hood_id({ id: currentEntity.Id }));
          } else if (currentEntity.EntityTypeKey === EntityTypeKey.GastroFlowModule) {
            this.store.dispatch(ProjectDataActions.set_last_selected_module_id({ id: currentEntity.Id }));

            if (currentEntity.ParentId) {
              this.store.dispatch(ProjectDataActions.set_last_selected_hood_id({ id: currentEntity.ParentId }));
            }
          } else if (currentEntity.Parent && currentEntity.Parent.EntityTypeKey === EntityTypeKey.GastroFlowModule) {
            this.store.dispatch(ProjectDataActions.set_last_selected_entity_id({ id: currentEntity.Id }));
            this.store.dispatch(ProjectDataActions.set_last_selected_module_id({ id: currentEntity.Parent.Id }));

            if (currentEntity.Parent.ParentId) {
              this.store.dispatch(ProjectDataActions.set_last_selected_hood_id({ id: currentEntity.Parent.ParentId }));
            }
          }
        } else {
          this.store.dispatch(ProjectDataActions.set_last_selected_entity_id({ id: null }));
        }

        return project;
      }),
      filter(value => !!value)
    );

    this.projectTreeEntity$ = this.store.select(selectProductPageProductTreeData).pipe(
      withLatestFrom(this.store.select(selectEntityTypesState)),
      map(([project, entityTypesState]) => {
        if (!project || !entityTypesState.loaded) {
          return null;
        }
        return project;
      }),
      filter(value => !!value)
    );

    this.loading$ = this.store.select(selectProjectEntityLoading);
    this.latestCreatedId$ = this.store.select(selectLatestCreatedId);
    this.latestCopiedId$ = this.store.select(selectLatestCopyInformation);
    this.latestDeletedIdList$ = this.store.select(selectLatestDeletedIdList);

    this.sidebarOpened$ = this.store.select(selectUIProductSidebarOpened);
    this.sidebarRightOpened$ = this.store.select(selectUIProductSidebarRightOpened);

    this.multiEditEnabled$ = this.store.select(selectMultiEditEnabled);
    this.show3DStatus$ = this.store.select(select3DViewStatus);

    this.multiEditHoodIds$ = this.store.select(selectMultiEditSelectedHoodIds);

    this.entityTypeNames$ = this.store.select(selectEntityTypesState).pipe(
      map((state): Record<string, string> => {
        if (!state.loaded) {
          return {};
        }

        const names: Record<string, string> = {};

        for (const entityTypeKey in state.entityTypes) {
          names[entityTypeKey] = this.translateService.instant(state.entityTypes[entityTypeKey].NameId);
        }

        return names;
      })
    );

    this.panelErrorIds$ = this.store
      .select(selectProjectFeedbackMessages)
      .pipe(map(messages => messages.filter(m => m.Parent.EntityTypeKey === EntityTypeKey.GastroFlowOutsidePanel).map(m => m.ParentId)));

    const resultReceived = this.loading$.pipe(filter(result => !result));

    this.showLoading$ = merge(
      timer(350).pipe(
        map(() => true),
        takeUntil(resultReceived)
      ),
      resultReceived.pipe(map(() => false))
    );

    this.setSidenavSettings();

    this.subscriptions.push(
      this.multiEditEnabled$.subscribe(enabled => {
        if (enabled) {
          this.interactiveComponentHandler.setSelectedEntityIds([]);
        }
      })
    );

    this.subscriptions.push(
      this.entity3DClicked$
        .pipe(
          withLatestFrom(
            this.projectEntity$,
            this.multiEditEnabled$,
            this.store.select(selectMultiEditSelectedEntityIds),
            this.store.select(selectMultiEditEntityTypeKey)
          )
        )
        .subscribe(([clickedEntityId, project, multiEditEnabled, selectedMultiEntityIds, multiEditTypeKey]) => {
          if (multiEditEnabled) {
            if (selectedMultiEntityIds.includes(clickedEntityId)) {
              this.store.dispatch(ProductViewActions.multi_edit_entity_selection_changed({ selected: false, entityId: clickedEntityId }));
            } else {
              const entity = ProductTreeTraverseService.findInTreeById(project, clickedEntityId);
              if (entity.EntityTypeKey === multiEditTypeKey) {
                this.store.dispatch(ProductViewActions.multi_edit_entity_selection_changed({ selected: true, entityId: clickedEntityId }));
              }
            }
          } else {
            this.router.navigate(['.'], {
              queryParams: { selectedId: clickedEntityId },
              relativeTo: this.route,
              onSameUrlNavigation: 'reload'
            });
            this.interactiveComponentHandler.setSelectedEntityIds(clickedEntityId);
          }
        })
    );

    this.subscriptions.push(
      this.projectEntity$
        .pipe(withLatestFrom(this.multiEditEnabled$, this.store.select(selectMultiEditSelectedEntityIds), this.multiEditHoodIds$))
        .subscribe(([project, multiEditEnabled, multiEditSelectedEntityIds, hoodIds]) => {
          if (multiEditEnabled && multiEditSelectedEntityIds.length > 0) {
            const empytySelectionIds = multiEditSelectedEntityIds.filter(id => this.findInTreeById(project, id) === null);

            if (empytySelectionIds.length > 0 && hoodIds.length === 1) {
              const hood = project.Children.find(
                entity => entity.EntityTypeKey === EntityTypeKey.GastroFlowHood && entity.Id === hoodIds[0]
              );

              if (hood) {
                this.store.dispatch(ProductViewActions.select_multi_edit_entity({ entityId: hood.Id, typeKey: hood.EntityTypeKey }));
              }
            }
          }
        })
    );

    this.subscriptions.push(
      this.store
        .select(selectMultiEditSelectedEntityIds)
        .pipe(withLatestFrom(this.projectEntity$, this.multiEditEnabled$))
        .subscribe(([selectedIds, project, enabled]) => {
          if (enabled) {
            const hoodIds = [];

            const hoods = project.Children.filter(x => x.EntityTypeKey === EntityTypeKey.GastroFlowHood);

            hoods.forEach(hood => {
              if (selectedIds.some(id => this.findInTreeById(hood, id) !== null)) {
                hoodIds.push(hood.Id);
              }
            });

            this.store.dispatch(ProductViewActions.set_multi_entity_hood_ids({ hoodIds }));
          }
        })
    );

    this.subscriptions.push(
      this.projectLoaded$.subscribe(loaded => {
        if (loaded) {
          this.watchEmptyEntity();
        }
      })
    );

    this.subscriptions.push(
      this.store
        .select(selectMultiEditSelectedEntityIds)
        .pipe(withLatestFrom(this.multiEditEnabled$))
        .subscribe(([selectedIds, enabled]) => {
          if (enabled) {
            this.interactiveComponentHandler.setSelectedEntityIds(selectedIds);
          }
        })
    );
  }

  shouldRenderGroupFilter(group: PropertyGroupType): boolean {
    if (group.Key.includes('ConfigureHoodLines')) return false;
    return true;
  }

  setSidenavSettings() {
    /* 8 is top and bottom margins */
    this.sidenavSettings.height = document.body.parentElement.offsetHeight - this.sidenavSettings.top - this.sidenavSettings.bottom - 8;
    /* 335 is product messages height (300), top and bottom paddings (each 16px), borders (1px *2) and divider height */
    this.sidenavSettings.maxHeightProductTree = this.sidenavSettings.height - 335;

    document.addEventListener('scroll', () => {
      /* When opening element in overlay (e.g. dialog), scrollTop and scrollHeight changes values.
       * Therefore, topPosition and topMax values are added.
       * Also, instead of using document.body.parentElement.scrollHeight, document.body.scrollHeight is used.
       */
      const overlayElement = document.querySelector('.cdk-global-scrollblock');
      let topPosition = 0;
      if (overlayElement) {
        topPosition = parseFloat(window.getComputedStyle(overlayElement).top.slice(0, -2));
      }

      this.sidenavSettings.top = Math.max(0, this.headerHeight - (document.body.parentElement.scrollTop + topPosition * -1));
      const topMax = Math.max(document.body.parentElement.scrollTop, -topPosition);
      this.sidenavSettings.bottom = Math.max(
        0,
        topMax + document.body.parentElement.offsetHeight + this.footerHeight - document.body.scrollHeight
      );
      this.sidenavSettings.height = document.body.parentElement.offsetHeight - this.sidenavSettings.top - this.sidenavSettings.bottom;
      this.sidenavSettings.maxHeightProductTree = this.sidenavSettings.height - 335;
    });
  }

  initializeComponent() {
    this.projectId = this.route.snapshot.paramMap.get('id');
    this.store.dispatch(ProjectDataActions.get_project_start({ id: this.projectId }));
  }

  ngOnInit(): void {
    this.setBreadcrumbs();
    this.watchLatestCreatedEntity();
    this.watchLatestCopiedProduct();
    this.watchLatestDeletedProducts();
    this.store.dispatch(EntityTypesActions.entity_types_required());
  }

  ngOnDestroy(): void {
    this.store.dispatch(ProductViewActions.disable_multi_edit());
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.emptyEntitySubscription?.unsubscribe();
  }

  watchLatestCreatedEntity() {
    /*
     * When a new entity is created, navigate to the product view with latest created entity selected;
     */
    this.subscriptions.push(
      this.latestCreatedId$
        .pipe(
          map(id => {
            if (id !== null) {
              this.store.dispatch(ProjectDataActions.reset_latest_created_id());
              this.router.navigate(['/projects', this.projectId, 'products'], {
                queryParams: { selectedId: id }
              });
            }
          })
        )
        .subscribe()
    );
  }

  watchEmptyEntity() {
    if (!this.emptyEntitySubscription) {
      this.emptyEntitySubscription = this.projectEntity$
        .pipe(
          withLatestFrom(
            this.store.select(selectLastSelectedHoodId),
            this.store.select(selectLastSelectedModuleId),
            this.store.select(selectLastSelectedEntityId)
          )
        )
        .subscribe(([project, hoodId, moduleId, entityId]) => {
          const currentEntity = this.getSelectedEntity(project);

          if (currentEntity === null) {
            const selectedId = this.route.snapshot.queryParamMap.get('selectedId');

            const currentEntityEmpty = selectedId === entityId;

            if (currentEntityEmpty) {
              this.store.dispatch(ProjectDataActions.set_last_selected_entity_id({ id: null }));
            }

            const redirectId = (currentEntityEmpty ? null : entityId) ?? moduleId ?? hoodId;

            if (redirectId) {
              this.router.navigate([`/projects/${project.Id}/products`], { queryParams: { selectedId: redirectId } });
            }
          }
        });
    }
  }

  watchLatestCopiedProduct() {
    /*
     * When a new entity is copied, navigate to the product view with latest copied entity selected;
     */
    this.subscriptions.push(
      this.latestCopiedId$
        .pipe(
          map(({ copiedId, projectId }) => {
            if (copiedId !== null) {
              this.store.dispatch(ProjectDataActions.reset_latest_copied_id());
              if (projectId && projectId === this.projectId) {
                // Only navigate to copied product if it's in the current project
                this.router.navigate(['/projects', this.projectId, 'products'], {
                  queryParams: { selectedId: copiedId }
                });
              }
            }
          })
        )
        .subscribe()
    );
  }

  watchLatestDeletedProducts() {
    /*
     * When a product is deleted, change query params depending on what was selected before deletion;
     * - If one of the deleted entities was selected, selectedId should not be set (nothing is selected)
     * - If none of entities was selected, selectedId should not be set
     * - If some other than deleted entities was selected, the same selectedId should remain.
     */
    this.subscriptions.push(
      this.latestDeletedIdList$
        .pipe(
          map(idList => {
            if (idList.length > 0) {
              this.store.dispatch(ProjectDataActions.reset_latest_deleted_id());
              const selectedId = this.route.snapshot.queryParamMap.get('selectedId');
              if (idList.includes(selectedId) || selectedId === null) {
                this.router.navigate(['/projects', this.projectId, 'products'], { onSameUrlNavigation: 'reload' });
              } else {
                this.router.navigate(['/projects', this.projectId, 'products'], {
                  queryParams: { selectedId },
                  onSameUrlNavigation: 'reload'
                });
              }
            }
          })
        )
        .subscribe()
    );
  }

  entity3DClicked(entityId: string): void {
    this.entity3DClicked$.next(entityId);
  }

  hoodLinesButtonClicked(): void {
    this.route.queryParams.subscribe(params => {
      this.productId = params['selectedId'];
      this.router.navigate(['./addline'], { queryParams: { selectedId: this.productId }, relativeTo: this.route });
    });
  }

  showHoodLineButton(entity: TreeEntity): boolean {
    return this.getSelectedEntity(entity)?.EntityTypeKey === EntityTypeKey.GastroFlowHood;
  }

  isNotHoodLineMessage(message: TreeEntity): boolean {
    return (
      message.Parent?.EntityTypeKey !== EntityTypeKey.GastroFlowHoodLine &&
      message.Parent?.EntityTypeKey !== EntityTypeKey.GastroFlowHoodConnectionLine
    );
  }

  getSelectedEntity(entity: TreeEntity): TreeEntity {
    const selectedId = this.route.snapshot.queryParamMap.get('selectedId');

    if (selectedId) {
      return this.findInTreeById(entity, selectedId);
    } else {
      return null;
    }
  }

  findInTreeById(selector: TreeEntity, id: string): TreeEntity {
    if (selector === null) {
      return null;
    }
    if (selector.Id === id) {
      return selector;
    }
    for (const child of selector.Children) {
      const candidate = this.findInTreeById(child, id);
      if (candidate !== null) {
        return candidate;
      }
    }
    return null;
  }

  setBreadcrumbs() {
    this.projectEntity$.subscribe((projectEntity: TreeEntity) => {
      const projectBreadcrumb: Breadcrumb = { url: `/projects/${this.projectId}`, text: '', translatable: false, isCurrent: false };
      const nameProperty = projectEntity?.Properties['Name'];
      const name = (nameProperty?.Value as string) || '';
      if (name) {
        projectBreadcrumb.text = name;
        projectBreadcrumb.translatable = false;
      } else {
        projectBreadcrumb.text = '-';
        projectBreadcrumb.translatable = false;
      }
      this.breadcrumbs = [
        { url: '/projects', text: 'UI.Common.Breadcrumb.Projects', translatable: true, isCurrent: false },
        projectBreadcrumb,
        { url: `/projects/${this.projectId}/products`, text: 'UI.Common.Breadcrumb.Products', translatable: true, isCurrent: true }
      ];
    });
  }
}
